<template>
  <div :style="{ padding: padding }" class="payment-amount-container">
    <div class="d-flex justify-center" @click="togglePopup">
      <div class="d-flex">
        <span
          :class="totalAmountClass"
          style="
            cursor: pointer;
            min-width: 110px;
            display: inline-block;
            text-align: center;
          "
        >
          {{ totalAmount | currency }}
        </span>

        <!-- Penalty chip - Hidden for supplier view (suppliers should only see original invoice amount) -->
        <div v-if="shouldShowPenaltyDetails" class="ml-2">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip small class="light" v-bind="attrs" v-on="on">
                <b>{{ penaltyPercentage }}%</b>
              </v-chip>
            </template>
            <span>
              Este interés por mora del {{ penaltyPercentage }}% se aplica por
              retraso en el pago.
            </span>
          </v-tooltip>
        </div>
        <!-- Info icon - Hidden for supplier view when only original amount is shown -->
        <v-icon
          v-if="!useExternalToggle && !shouldShowOnlyOriginalAmount"
          small
          class="ml-1 info-icon"
          :color="popupVisible ? 'primary' : ''"
          :title="'Ver detalles del pago'"
        >
          {{ popupVisible ? "mdi-information" : "mdi-information-outline" }}
        </v-icon>
      </div>
    </div>
    <!-- Popup con detalles -->
    <transition name="fade">
      <div
        v-if="!useExternalToggle && popupVisible"
        class="payment-popup"
        v-click-outside="closePopup"
        ref="paymentPopup"
      >
        <div class="popup-content">
          <div class="detail-row">
            <span class="font-weight-medium">Monto original facturado:</span>
            <span class="font-weight-bold">{{
              originalAmount | currency
            }}</span>
          </div>
          <!-- Following details hidden for supplier view -->
          <div class="detail-row" v-if="paidAmount > 0 && !shouldShowOnlyOriginalAmount">
            <span class="font-weight-medium">Pagos realizados:</span>
            <span class="font-weight-bold">{{ paidAmount | currency }}</span>
          </div>
          <div class="detail-row" v-if="!shouldShowOnlyOriginalAmount">
            <span class="font-weight-medium">Saldo pendiente:</span>
            <span class="font-weight-bold">{{ capitalPendingAmount | currency }}</span>
          </div>
          <div class="detail-row" v-if="shouldShowPenaltyDetails">
            <span class="font-weight-medium"
              >Interés por mora ({{ penaltyPercentage }}%):</span
            >
            <span class="font-weight-bold">{{ penaltyAmount | currency }}</span>
          </div>
          <div class="detail-row font-weight-bold" v-if="shouldShowPenaltyDetails">
            <span class="font-weight-medium">Total a liquidar:</span>
            <span class="font-weight-bold">{{ totalPendingAmount | currency }}</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- Detailed breakdown para uso con componentes externos -->
    <div
      v-if="showDetails && useExternalToggle"
      style="font-size: 0.8em; text-align: left; width: 100%"
      class="payment-details"
    >
      <div class="detail-row">
        <span class="font-weight-medium">Monto original facturado:</span>
        <span class="font-weight-bold">{{ originalAmount | currency }}</span>
      </div>
      <!-- Following details hidden for supplier view -->
      <div class="detail-row" v-if="paidAmount > 0 && !shouldShowOnlyOriginalAmount">
        <span class="font-weight-medium">Pagos realizados:</span>
        <span class="font-weight-bold">{{ paidAmount | currency }}</span>
      </div>
      <div class="detail-row" v-if="!shouldShowOnlyOriginalAmount">
        <span class="font-weight-medium">Saldo pendiente:</span>
        <span class="font-weight-bold">{{ capitalPendingAmount | currency }}</span>
      </div>
      <div class="detail-row" v-if="shouldShowPenaltyDetails">
        <span class="font-weight-medium"
          >Interés por mora ({{ penaltyPercentage }}%):</span
        >
        <span class="font-weight-bold">{{ penaltyAmount | currency }}</span>
      </div>
      <div class="detail-row font-weight-bold" v-if="shouldShowPenaltyDetails">
        <span class="font-weight-medium">Total a liquidar:</span>
        <span class="font-weight-bold">{{ totalPendingAmount | currency }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.payment-amount-container {
  position: relative;
}

.payment-details {
  padding: 6px;
  margin-top: 8px;
  width: 100%; /* Asegura que ocupe el ancho disponible */
}

.payment-popup {
  position: fixed;
  z-index: 9999;
  min-width: 250px;
  margin-top: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.popup-content {
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #eee;
  background-color: white;
  font-size: 0.85em;
  text-align: left;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  white-space: nowrap;
}

/* Estilo para icono de información */
.info-icon {
  cursor: pointer;
}

/* Transición para el popup */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
<script>
export default {
  name: "PaymentAmount",
  props: {
    payment: {
      type: Object,
      required: true,
    },
    totalAmountClass: {
      type: String,
      default: "",
    },
    showDetails: {
      type: Boolean,
      default: false,
    },
    padding: {
      type: String,
      default: "0",
    },
    useExternalToggle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      popupVisible: false,
    };
  },

  mounted() {
    window.addEventListener("scroll", this.closePopup);
    window.addEventListener("resize", this.closePopup);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.closePopup);
    window.removeEventListener("resize", this.closePopup);
  },

  directives: {
    clickOutside: {
      bind(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener("click", el.clickOutsideEvent, true);
      },
      unbind(el) {
        document.removeEventListener("click", el.clickOutsideEvent, true);
      },
    },
  },
  methods: {
    togglePopup() {
      if (!this.useExternalToggle) {
        this.popupVisible = !this.popupVisible;

        if (this.popupVisible) {
          this.$nextTick(() => {
            this.positionPopup();
          });
        }
      }
    },
    closePopup() {
      this.popupVisible = false;
    },
    positionPopup() {
      const popup = this.$refs.paymentPopup;
      if (!popup) return;

      const container = this.$el.getBoundingClientRect();

      popup.style.top = `${container.bottom}px`;
      popup.style.left = `${container.left}px`;
    },
  },
  filters: {
    currency(value) {
      if (value === null || value === undefined) return "$0.00";
      var formatter = new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return formatter.format(value);
    },
  },
  computed: {
    shouldShowOnlyOriginalAmount() {
      // Returns true for supplier view to simplify payment display
      return this.$UserManager.getMainRole() === 'supplier' || this.isPaid;
    },
    originalAmount() {
      return parseFloat(this.payment?.amount || 0);
    },
    paidAmount() {
      return parseFloat(this.payment?.paid_amount || 0);
    },
    penaltyPercentage() {
      return parseFloat(this.payment?.penalty_percentage || 0);
    },
    hasPenalty() {
      return this.penaltyPercentage > 0;
    },
    shouldShowPenaltyDetails() {
      // Returns true when penalty details should be displayed
      // Combines: has penalty, not paid, and user has permission to see full details
      return this.hasPenalty && !this.shouldShowOnlyOriginalAmount;
    },
    capitalPendingAmount() {
      // Usamos el valor que viene de la API para el capital pendiente
      return parseFloat(this.payment?.capital_pending_amount || 0);
    },
    pendingAmount() {
      // Mantenemos este método para compatibilidad hacia atrás
      // pero ahora usa capital_pending_amount si está disponible
      if (this.payment?.capital_pending_amount !== undefined) {
        return this.capitalPendingAmount;
      }
      // Fallback al cálculo anterior si no está disponible el nuevo campo
      if (this.isPaid) {
        return 0;
      }
      return Math.max(0, this.originalAmount - this.paidAmount);
    },
    penaltyAmount() {
      // Usamos el valor que viene de la API en lugar de calcularlo
      return parseFloat(this.payment?.penalty_amount || 0);
    },
    totalPendingAmount() {
      // Usamos el valor que viene de la API para el total pendiente
      return parseFloat(this.payment?.total_pending_amount || 0);
    },
    totalAmount() {
      // If fully paid or in supplier view, show original amount only
      if (this.shouldShowOnlyOriginalAmount) {
        return this.originalAmount;
      }
      // Si tiene penalización, mostramos el total pendiente (incluye capital + penalizaciones)
      if (this.hasPenalty) {
        return this.totalPendingAmount;
      }
      // Si no hay penalización, mostramos el capital pendiente
      return this.capitalPendingAmount;
    },
    isPaid() {
      return (
        this.payment?.status?.name === "paid" ||
        this.payment?.status?.name === "paid_by_mangxo"
      );
    },
  },
};
</script>
